import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useUpdatePlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/api/useUpdatePlacementCandidate";
import { JobType, type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect, useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { type PlacementWithDetails } from "../../api/useFetchPaginatedPlacements";

export const payAndScheduleFormSchema = z.object({
  yearsOfExperienceByQualification: z.record(z.string(), z.number().optional()).optional(),
  useCombinedBenefit: z.boolean().optional(),
  useHealthInsuranceBenefit: z.boolean().optional(),
  usePaidTimeOffBenefit: z.boolean().optional(),
  jobType: z.nativeEnum(JobType).optional(),
});

export type PayAndScheduleFormData = z.infer<typeof payAndScheduleFormSchema>;

export interface LegacyPayAndScheduleFormData {
  yearsOfExperience?: number | undefined;
  useCombinedBenefit?: boolean | undefined;
  useHealthBenefit?: boolean | undefined;
  usePtoBenefit?: boolean | undefined;
  jobType?: JobType | undefined;
}

export interface UsePayAndScheduleFormArguments {
  placementDetails: Pick<PlacementWithDetails, "jobShifts"> | undefined;
  placementCandidate:
    | Pick<
        PlacementCandidate,
        "id" | "yearsOfExperienceByQualification" | "payAndScheduleFormData" | "worker"
      >
    | undefined;
}

export interface UsePayAndScheduleFormReturn {
  useFormReturn: UseFormReturn<PayAndScheduleFormData>;
  isUpdating: boolean;
  updatePlacementCandidateFormData: (formData: PayAndScheduleFormData) => void;
}

// was used on app versions <= 2.223
export function useLegacySavedPayAndScheduleFormData() {
  return useLocalStorage<LegacyPayAndScheduleFormData>("placement-pay-and-schedule-form-data", {
    yearsOfExperience: undefined,
    useCombinedBenefit: undefined,
    useHealthBenefit: undefined,
    usePtoBenefit: undefined,
    jobType: undefined,
  });
}

export function usePayAndScheduleForm({
  placementDetails,
  placementCandidate,
}: UsePayAndScheduleFormArguments): UsePayAndScheduleFormReturn {
  const [legacySavedFormData] = useLegacySavedPayAndScheduleFormData();

  const { mutateAsync: updatePlacementCandidate, isLoading: isUpdating } =
    useUpdatePlacementCandidate();

  const worker = useDefinedWorker();

  const savedYearsOfExperience = isDefined(placementCandidate)
    ? placementCandidate.yearsOfExperienceByQualification
    : undefined;
  const savedCombinedBenefits = isDefined(placementCandidate)
    ? placementCandidate.payAndScheduleFormData?.benefits?.combined
    : undefined;
  const savedHealthInsuranceBenefits = isDefined(placementCandidate)
    ? placementCandidate.payAndScheduleFormData?.benefits?.healthInsurance
    : undefined;
  const savedPaidTimeOffBenefits = isDefined(placementCandidate)
    ? placementCandidate.payAndScheduleFormData?.benefits?.paidTimeOff
    : undefined;
  const savedJobType = isDefined(placementCandidate)
    ? placementCandidate.payAndScheduleFormData?.jobType
    : undefined;

  const defaultValue: PayAndScheduleFormData = useMemo(() => {
    const legacyYearsOfExperienceByQualification = isDefined(legacySavedFormData.yearsOfExperience)
      ? worker.licensesData.reduce<Record<string, number>>((accumulator, license) => {
          accumulator[license.qualification] = legacySavedFormData.yearsOfExperience!;
          return accumulator;
        }, {})
      : undefined;

    return {
      yearsOfExperienceByQualification:
        savedYearsOfExperience ?? legacyYearsOfExperienceByQualification,
      useCombinedBenefit: savedCombinedBenefits ?? legacySavedFormData.useCombinedBenefit,
      useHealthInsuranceBenefit:
        savedHealthInsuranceBenefits ?? legacySavedFormData.useHealthBenefit,
      usePaidTimeOffBenefit: savedPaidTimeOffBenefits ?? legacySavedFormData.usePtoBenefit,
      jobType: savedJobType ?? legacySavedFormData.jobType,
    };
  }, [
    legacySavedFormData.jobType,
    legacySavedFormData.useCombinedBenefit,
    legacySavedFormData.useHealthBenefit,
    legacySavedFormData.usePtoBenefit,
    legacySavedFormData.yearsOfExperience,
    savedCombinedBenefits,
    savedHealthInsuranceBenefits,
    savedJobType,
    savedPaidTimeOffBenefits,
    savedYearsOfExperience,
    worker.licensesData,
  ]);

  const useFormReturn = useForm<PayAndScheduleFormData>({
    resolver: zodResolver(payAndScheduleFormSchema),
    defaultValues: defaultValue,
  });

  const { setValue, watch } = useFormReturn;

  const { jobType } = watch();

  const placementCandidateId = placementCandidate?.id;
  const workerId = placementCandidate?.worker.id;

  useEffect(() => {
    if (!placementDetails) {
      return;
    }

    const offeredJobTypes = new Set(
      placementDetails.jobShifts.map((jobShift) => jobShift.jobType) ?? []
    );

    // leftmost should get selected by default first
    const orderedJobTypes = [JobType.FULL_TIME, JobType.PART_TIME, JobType.PER_DIEM].filter(
      (jobType) => offeredJobTypes.has(jobType)
    );

    if ((!jobType || !orderedJobTypes.includes(jobType)) && orderedJobTypes.length > 0) {
      setValue("jobType", orderedJobTypes[0]);
    }
  }, [placementDetails, jobType, setValue]);

  async function updatePlacementCandidateFormData(formData: PayAndScheduleFormData) {
    if (!placementCandidateId || !workerId) {
      return;
    }

    const {
      jobType,
      yearsOfExperienceByQualification,
      useCombinedBenefit,
      useHealthInsuranceBenefit,
      usePaidTimeOffBenefit,
    } = formData;

    await updatePlacementCandidate({
      placementCandidateId,
      workerId,
      yearsOfExperienceByQualification:
        yearsOfExperienceByQualification ?? placementCandidate.yearsOfExperienceByQualification,
      payAndScheduleFormData: {
        jobType: jobType ?? placementCandidate.payAndScheduleFormData?.jobType,
        benefits: {
          combined:
            useCombinedBenefit ?? placementCandidate.payAndScheduleFormData?.benefits?.combined,
          healthInsurance:
            useHealthInsuranceBenefit ??
            placementCandidate.payAndScheduleFormData?.benefits?.healthInsurance,
          paidTimeOff:
            usePaidTimeOffBenefit ??
            placementCandidate.payAndScheduleFormData?.benefits?.paidTimeOff,
        },
      },
    });
  }

  return { useFormReturn, updatePlacementCandidateFormData, isUpdating };
}
